type SymphonyMarkProps = {
  size?: number;
  /** Tile + form treatment. 'forms' draws the loop alone (no tile), for
      placing on a surface that already provides the green. 'light' also drops
      the tile but inks the loop in the current theme's primary text colour, for
      a dark surface (e.g. the mobile drawer) — monochrome, no green. */
  variant?: 'color' | 'black' | 'outline' | 'forms' | 'light';
};

// The Symphony mark (official brand icon): eight marks ringing an open center
// that MORPH from a sharp square (top) progressively into full circles (right
// side) and back — the system's two primitives (square = agent, circle =
// person) resolving into one another around a continuous loop. Rendered inside
// the rounded-square app tile. Geometry is transcribed from the brand icon
// (Icon.svg), scaled into the 24×24 viewBox. See docs/symphony-brand-system.md.

// Tile color (brand green) and mark color, from the updated brand palette.
export const SYMPHONY_GREEN = 'var(--ds-fill-accent-brand-default)';
const MARK_DARK = 'var(--ds-neutral-800)';

// Eight marks, indexed clockwise from top (12 o'clock) — the same order the
// morph-to-hamburger (SymphonyMorphMark) and the loop loader rely on. Each
// carries its own `size` and corner `rx`, so the ring reads square → circle as
// the eye travels around it. `rx === size / 2` is a full circle.
export type SymphonyLoopMark = { cx: number; cy: number; size: number; rx: number };
export const SYMPHONY_LOOP_MARKS: SymphonyLoopMark[] = [
  { cx: 12.26, cy: 4.96, size: 3.65, rx: 0.24 },  // top — sharp square
  { cx: 17.06, cy: 7.13, size: 2.57, rx: 1.29 },  // top-right — small circle
  { cx: 19.06, cy: 12.13, size: 3.65, rx: 1.83 }, // right — circle
  { cx: 17.07, cy: 17.17, size: 3.65, rx: 1.58 }, // bottom-right
  { cx: 12.26, cy: 19.3, size: 3.65, rx: 1.28 },  // bottom
  { cx: 7.02, cy: 17.17, size: 3.65, rx: 1.04 },  // bottom-left
  { cx: 4.88, cy: 12.13, size: 3.65, rx: 0.79 },  // left
  { cx: 7.02, cy: 7.14, size: 3.65, rx: 0.55 },   // top-left — near-square
];

export function SymphonyMark({ size = 24, variant = 'color' }: SymphonyMarkProps) {
  const tileFill = variant === 'color' ? SYMPHONY_GREEN : variant === 'black' ? MARK_DARK : 'none';
  const tileStroke = variant === 'outline' ? 'rgba(var(--ds-neutral-800-rgb), 0.55)' : 'none';
  const formFill = variant === 'black' ? 'var(--ds-white)' : variant === 'light' ? 'var(--ds-text-primary)' : MARK_DARK;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Symphony"
      role="img"
    >
      <rect x="1" y="1" width="22" height="22" rx="6.5" fill={tileFill} stroke={tileStroke} strokeWidth="1" />
      {SYMPHONY_LOOP_MARKS.map((m, i) => (
        <rect
          key={i}
          x={m.cx - m.size / 2}
          y={m.cy - m.size / 2}
          width={m.size}
          height={m.size}
          rx={m.rx}
          fill={formFill}
        />
      ))}
    </svg>
  );
}
